import React, { useEffect, useRef } from "react";
import cn from "classnames";
import { useComboxContext } from "~/components/shared/Combox/ComboxContext";
import Icon from "~/components/shared/Icon";

export interface ComboxItemProps {
  children: React.ReactNode;
  value: string;
  className?: string;
  disabled?: boolean;
  itemIndex?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

export const ComboxItem = ({
  children,
  value,
  className,
  disabled = false,
  itemIndex = -1,
  onMouseEnter: customOnMouseEnter,
  onMouseLeave: customOnMouseLeave,
  onFocus: customOnFocus,
  onBlur: customOnBlur,
  ...props
}: ComboxItemProps) => {
  const {
    selectedValue,
    selectedValues,
    onValueChange,
    highlightedIndex,
    setHighlightedIndexWithMethod,
    highlightMethod,
    open,
    multiple,
    searchInputRef,
  } = useComboxContext();
  const itemRef = useRef<HTMLDivElement>(null);

  // Check if this item should be highlighted by comparing with DOM position when filtering
  const { searchQuery } = useComboxContext();
  const isHighlighted = React.useMemo(() => {
    if (searchQuery && itemRef.current) {
      // When filtering, use DOM position instead of itemIndex
      const container = itemRef.current.closest('[role="listbox"]');
      const allItems = container?.querySelectorAll("[data-combox-item]");
      if (allItems) {
        const domPosition = Array.from(allItems).indexOf(itemRef.current);
        return highlightedIndex === domPosition;
      }
    }
    // Default behavior: use itemIndex
    return highlightedIndex === itemIndex;
  }, [highlightedIndex, itemIndex, searchQuery]);

  const isSelected = multiple ? selectedValues.includes(value) : selectedValue === value;

  // Scroll highlighted item into view
  useEffect(() => {
    if (open && isHighlighted && itemRef.current) {
      const container = itemRef.current.closest('[role="listbox"]');
      const allItems = container?.querySelectorAll("[data-combox-item]");
      const actualIndex = allItems ? Array.from(allItems).indexOf(itemRef.current) : itemIndex;
      const totalItems = allItems?.length || 0;

      const scrollBehavior = actualIndex === totalItems - 1 ? "end" : "nearest";

      if (open && isHighlighted && itemRef.current && highlightMethod === "keyboard") {
        itemRef.current.scrollIntoView({ block: scrollBehavior });
      }
    }
  }, [isHighlighted, open, itemIndex, highlightMethod]);

  const handleClick = React.useCallback(() => {
    if (!disabled && onValueChange) {
      onValueChange(value);

      // In multi-select mode, keep focus on the search input after selection
      if (multiple && searchInputRef.current) {
        searchInputRef.current.focus({ preventScroll: true });
      }
    }
  }, [disabled, onValueChange, value, multiple, searchInputRef]);

  const handleMouseEnter = React.useCallback(() => {
    if (itemIndex >= 0 && !disabled) {
      setHighlightedIndexWithMethod(itemIndex, "mouse");
    }
    customOnMouseEnter?.();
  }, [itemIndex, disabled, setHighlightedIndexWithMethod, customOnMouseEnter]);

  return (
    <div
      ref={itemRef}
      role="option"
      aria-selected={isSelected}
      aria-disabled={disabled}
      data-combox-item={value}
      className={cn(
        "flex w-full items-center gap-2 rounded px-2 py-1 outline-none",
        !disabled && "cursor-pointer hover:bg-gray-200",
        disabled && "cursor-not-allowed opacity-50 hover:bg-gray-100",
        isHighlighted && !disabled && "bg-gray-200",
        isSelected &&
          !disabled &&
          "bg-purple-100 text-purple-600 hover:bg-purple-100 hover:text-purple-600",
        isSelected && isHighlighted && !disabled && "ring-1 ring-purple-300",
        className,
      )}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={customOnMouseLeave}
      onFocus={customOnFocus}
      onBlur={customOnBlur}
      style={{ scrollSnapAlign: "start" }}
      {...props}
    >
      {children}
      {isSelected && multiple && (
        <span className="ml-auto">
          <Icon icon="heroicons/16/solid/check" className="h-4 w-4" />
        </span>
      )}
    </div>
  );
};

export default ComboxItem;
